/**
 * Global Meeting Permissions Hook
 * Light QA Contract Compliant:
 * - Reuses existing permission check pattern
 * - No projectId required (global scope)
 * - Caching with staleTime for performance
 */

import { useAuthQuery } from '@/hooks/useAuthQuery';
import { useAuth } from '@/hooks/useAuth';
import { useAdminCheck } from '@/hooks/useAdminCheck';

/**
 * Check if user has MEETING_ACCESS permission globally
 * Admin users always have access
 */
export const useGlobalMeetingAccess = () => {
  const { user } = useAuth();
  const { isAdmin, isLoading: isAdminLoading } = useAdminCheck();

  const permissionQuery = useAuthQuery<boolean>({
    queryKey: ['global-meeting-access', user?.id],
    queryFn: async () => {
      const { checkUserPermission } = await import('@/lib/queries/authQueries');
      return checkUserPermission(user?.id || '', 'MEETING_ACCESS');
    },
    requiresAuth: true,
    requiresSession: true,
    enabled: !!user?.id && !isAdmin,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  // Admin override
  if (isAdmin) {
    return {
      data: true,
      isLoading: isAdminLoading,
      error: null,
    };
  }

  return permissionQuery;
};

/**
 * Check if user can view meetings globally
 */
export const useGlobalMeetingViewPermission = () => {
  const { user } = useAuth();
  const { isAdmin, isLoading: isAdminLoading } = useAdminCheck();

  const permissionQuery = useAuthQuery<boolean>({
    queryKey: ['global-meeting-view', user?.id],
    queryFn: async () => {
      const { checkUserPermission } = await import('@/lib/queries/authQueries');
      return checkUserPermission(user?.id || '', 'MEETING_VIEW');
    },
    requiresAuth: true,
    requiresSession: true,
    enabled: !!user?.id && !isAdmin,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  if (isAdmin) {
    return {
      data: true,
      isLoading: isAdminLoading,
      error: null,
    };
  }

  return permissionQuery;
};
